import { useEffect, useState } from 'react';

/**
 * Subscribes to a CSS media query, re-rendering when it starts or stops matching.
 *
 * The breakpoint hooks (`useCompactStage`, `usePortraitLock`) are thin wrappers
 * around this so the listener dance below lives in one place: `addListener` is the
 * iOS < 14 spelling of `addEventListener`, and the phones these layouts exist for
 * include older ones, so both spellings stay wired up.
 *
 * Starts `false` and corrects on mount rather than reading during render —
 * `window.matchMedia` is absent under SSR and in parts of the jsdom test setup, so
 * a missing implementation degrades to "does not match" instead of throwing.
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia?.(query);
    if (!mql) return;
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener?.(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener?.(onChange);
    };
  }, [query]);

  return matches;
};
